import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import PageComponentField from 'apps/analytics/components/PageComponentField';
import ProductType from 'apps/products/models/ProductType';
import ProductLabel from 'apps/products/models/ProductLabel';

import componentFields from 'apps/analytics/utils/componentFields';
import getSectionTitle from 'apps/fops/utils/getSectionTitle';

import { DEFAULT_FREQUENCY } from 'apps/subscriptions/components/fopPartials/SubscriptionsFrequencyOptions';

const hasDerivatives = (product) =>
  product.type === ProductType.SERVICE_COUNTER_PARENT || product.type === ProductType.PRODUCT_GROUP_PARENT;

const formatNumberWithDecimalPart = (number) => (Number(number).toFixed(2));

const getAnalyticsSection = (sectionAttributes) => {
  const title = getSectionTitle(sectionAttributes);
  return title ? title.replace(/»/g, '&raquo;') : undefined;
};

export function getProductFields(product) {
  const isOutOfStock = (product.labels || []).includes(ProductLabel.OOS) ? true : undefined;
  const promoId = get(product, 'offer.id');
  const couponId = get(product, 'coupon.id');
  const schemeId = get(product, 'offer.schemeId');
  const derivatives = hasDerivatives(product) ? true : undefined;
  const averageRating = formatNumberWithDecimalPart(get(product, 'reviewStats.averageRate', 0));
  const isSeasonal = product.seasonal ? true : undefined;
  return [
    <PageComponentField field={componentFields.SKU} value={product.sku} key="sku" />,
    <PageComponentField field={componentFields.IS_OOS} value={isOutOfStock} key="isoos" />,
    <PageComponentField field={componentFields.PROMO_ID} value={promoId} key="promoid" />,
    <PageComponentField field={componentFields.PER_COUPON_ID} value={couponId} key="couponid" />,
    <PageComponentField field={componentFields.CLUB_SCHEME_ID} value={schemeId} key="schemeid" />,
    <PageComponentField field={componentFields.HAS_DERIVATIVES} value={derivatives} key="derivatives" />,
    <PageComponentField field={componentFields.PRODUCT_AVERAGE_RATING} value={averageRating} key="averagerating" />,
    <PageComponentField
      field={componentFields.FAVOURITE_RATING}
      value={product.favouriteRating}
      key="favouriterating"
    />,
    <PageComponentField
      field={componentFields.RECOMMENDATION_RANK}
      value={product.recommendationRank}
      key="recommendationrank"
    />,
    <PageComponentField field={componentFields.IS_SEASONAL_SKU} value={isSeasonal} key="seasonal" />,
  ];
}

export const getProductFieldsPropTypes = {
  product: PropTypes.shape({
    sku: PropTypes.string.isRequired,
    labels: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ProductLabel))),
    offer: PropTypes.shape({
      id: PropTypes.string,
      schemeId: PropTypes.number,
    }),
    type: PropTypes.oneOf(Object.keys(ProductType)),
    reviewStats: PropTypes.shape({
      averageRate: PropTypes.number,
    }),
    seasonal: PropTypes.bool,
    favouriteRating: PropTypes.number,
    recommendationRank: PropTypes.number,
  }).isRequired,
};

export function getCommonFields(collectionOrderDetails) {
  const listSequence = collectionOrderDetails.fopInCollection;
  return [
    <PageComponentField field={componentFields.LIST_SEQUENCE} value={listSequence} key="listsequence" />,
  ];
}

export function getSectionFields(section, collectionOrderDetails) {
  const clusterTags = get(section, 'attributes.cluster.tags');
  const sectionId = clusterTags ? clusterTags.map(tag => tag.id).join('sub') : undefined;
  const sectionSequence = collectionOrderDetails.fopInSection;
  const sectionNumber = collectionOrderDetails.sectionInCollection;
  return [
    <PageComponentField
      field={componentFields.SECTION}
      value={getAnalyticsSection(section.attributes)}
      key="section"
    />,
    <PageComponentField field={componentFields.SECTION_ID} value={sectionId} key="sectionid" />,
    <PageComponentField field={componentFields.SECTION_SEQUENCE} value={sectionSequence} key="sectionsequence" />,
    <PageComponentField field={componentFields.SECTION_NUMBER} value={sectionNumber} key="sectionnumber" />,
  ];
}

export function getFeaturedFields(fop) {
  const description = get(fop, 'fopAttributes.featured_description');
  const advertParentDocNo = get(fop, 'fopAttributes.featured_advert_parent_doc');
  const advertParentDocVersion = get(fop, 'fopAttributes.featured_advert_parent_doc_version');
  return [
    <PageComponentField field={componentFields.DESCRIPTION} value={description} key="description" />,
    <PageComponentField
      field={componentFields.ADVERT_PARENT_DOC_NO}
      value={advertParentDocNo}
      key="advertparentdocno"
    />,
    <PageComponentField
      field={componentFields.ADVERT_PARENT_DOC_VERSION_NO}
      value={advertParentDocVersion}
      key="advertparentdocversion"
    />,
  ];
}

export function getSubscriptionFields(subscription) {
  if (!subscription) {
    return [
      <PageComponentField field="SUBS_SUGGESTED_FREQ" value={DEFAULT_FREQUENCY} key="SUBS_SUGGESTED_FREQ" />,
    ];
  }

  const { frequency, quantity } = subscription;
  return [
    <PageComponentField field="SUBS_FREQ" value={frequency} key="SUBS_FREQ" />,
    <PageComponentField field="SUBS_QUANTITY" value={quantity} key="SUBS_QUANTITY" />,
  ];
}

